'use client';

/**
 * HubCtas — SPEC-156 (Sprint 79, 2026-09-08).
 *
 * Renders the 7 configurable CTAs on the public Profile Hub (`/{username}`).
 * Reads the user's `hubSettings.ctas` configuration + renders only the
 * enabled CTAs. Each click is tracked via the existing
 * `POST /api/public/:username/click` endpoint (extended in SPEC-156 to
 * accept `ctaSlug` for per-CTA breakdown analytics).
 *
 * CTAs rendered (when enabled):
 *   1. Plan de Compensación → /plan-de-compensacion?ref={username}
 *   2. Mis Landings → /l/{username}
 *   3. Próxima Sala → /app/conferences (deep-link to user's scheduled rooms)
 *   4. Calendario Reserva → external URL (calendarBookingUrl)
 *   5. Mi Market → /market/tienda/{storeSlug}
 *   6. WhatsApp Directo → wa.me link (uses social.whatsapp)
 *   7. CTA Personalizado → custom URL + label
 *
 * §6 Anti-Invención: pure UI. The CTAs link to EXISTING tools — no new
 * endpoints, no new business logic. Auto-show (P-279) is also pure UI —
 * the parent passes whether each CTA has content to show.
 *
 * Spec ref: SPEC-156 §P-275 + §P-279.
 */

import { Button } from '@/components/ui/button';
import {
  DollarSign,
  FileText,
  Video,
  Calendar,
  ShoppingBag,
  MessageCircle,
  Link as LinkIcon,
  ExternalLink,
  Store,
  UserPlus,
} from 'lucide-react';

export interface HubCtasConfig {
  planCompensacion: boolean;
  myLandings: boolean;
  nextRoom: boolean;
  calendarBooking: boolean;
  myMarket: boolean;
  myStore: boolean;
  whatsappDirect: boolean;
  showProfileiaCta: boolean;
  // V108 Bug #3 PR2: array of up to 5 custom CTAs (was single `customCta`
  // object pre-PR2). Backend schema migrated in PR1 (merged in 3055562d).
  // Each item is optional on the public Hub side (we render only items
  // with enabled=true + non-empty url + non-empty label).
  customCtas?: Array<{
    enabled: boolean;
    url: string;
    label: string;
  }>;
}

export interface HubCtasProps {
  username: string;
  storeSlug?: string;
  whatsappNumber?: string;
  calendarBookingUrl?: string;
  hasLandings?: boolean;
  hasNextRoom?: boolean;
  hasMarketProducts?: boolean;
  ctasConfig: HubCtasConfig;
  /** SPEC-173: user's published landings (slug + title). Used to show a
   * selector when the user has multiple landings. If 0 or 1, the CTA
   * is a simple link to /l/{username}. */
  landings?: Array<{ slug: string; title: string }>;
  /** V108 Bug #4 fix (SPEC-171): when `calendarBookingUrl` is empty but
   * `availability.enabled` is true, the calendarBooking CTA falls back to
   * the internal reservation page `/{username}/reservar` (NOT external URL).
   * Only the `enabled` field is consumed here — the full availability
   * schema (timezone, slots, durations, buffer, maxAdvanceDays) lives in
   * the backend (user/sub-schemas/profile.ts) and is exposed publicly via
   * landing-post.ts -> hubSettings.availability. */
  availability?: { enabled: boolean };
  // PR #1455: referralCode for 'Quiero ser parte de Profileia' CTA.
  // Used to build the /r/{referralCode} link. Falls back to /register if empty.
  referralCode?: string;
}

export function HubCtas({
  username,
  storeSlug,
  whatsappNumber,
  calendarBookingUrl,
  hasLandings = false,
  hasNextRoom = false,
  hasMarketProducts = false,
  ctasConfig,
  landings = [],
  availability,
  referralCode,
}: HubCtasProps) {
  // Build the list of CTAs to render. Each entry is conditionally included
  // based on the user's hubSettings.ctas config AND the auto-show
  // conditions (P-279: CTAs that require content only show when content
  // exists — e.g. "Mis Landings" only shows if user has ≥1 published landing).
  const ctas: Array<{
    slug: string;
    label: string;
    href: string;
    icon: typeof DollarSign;
    external?: boolean;
  }> = [];

  if (ctasConfig.planCompensacion) {
    ctas.push({
      slug: 'planCompensacion',
      label: 'Descubre como ganar con Profileia',
      href: `/plan-de-compensacion?ref=${encodeURIComponent(username)}`,
      icon: DollarSign,
    });
  }

  // SPEC-173 (Sprint 85, 2026-09-10): "Mis Landings" SIEMPRE se muestra
  // cuando toggle ON. Si el usuario no tiene landings propias, el link
  // va a /l/{username} que sirve la LANDING GENÉRICA del sistema.
  // Si tiene múltiples landings, el CTA muestra un selector (dropdown).
  if (ctasConfig.myLandings) {
    if (landings.length > 1) {
      // Multiple landings — show a selector dropdown via a simple <select>
      // styled to match the other CTAs. The user can choose which landing
      // to visit, or "Ver todas" which goes to /l/{username}.
      ctas.push({
        slug: 'myLandings',
        label: 'Mis Landing Pages',
        href: `/l/${encodeURIComponent(username)}`,
        icon: FileText,
      });
      // The actual selector is rendered in the JSX below (not as a simple link).
    } else {
      ctas.push({
        slug: 'myLandings',
        label: 'Ver mis landing pages',
        href: `/l/${encodeURIComponent(username)}`,
        icon: FileText,
      });
    }
  }

  // P-279 auto-show: only render "Próxima Sala" if user has scheduled rooms
  if (ctasConfig.nextRoom && hasNextRoom) {
    ctas.push({
      slug: 'nextRoom',
      label: 'Próxima sala en vivo',
      href: `/app/conferences`,
      icon: Video,
    });
  }

  // V108 Bug #4 fix (SPEC-171 fallback): the URL is OPTIONAL. When
  // `calendarBookingUrl` is empty but `availability.enabled` is true,
  // link to the internal reservation page `/{username}/reservar`
  // (NOT external URL). Priority: external URL takes precedence over
  // internal availability — users who already configured Cal.com/Calendly
  // keep their existing flow untouched. If neither URL nor availability
  // is set, the CTA does NOT render (same silent-fail behavior as before
  // this fix, but now covering both branches).
  // Root cause: SPEC-171 left URL optional in CtaToggles config UI BUT
  // this component kept requiring `calendarBookingUrl` non-empty — the
  // fallback promised in cta-toggles.tsx comments was NEVER implemented.
  if (ctasConfig.calendarBooking) {
    // V108 Bug #4 fix: prefer explicit truthiness checks over `Boolean(...)`
    // so TypeScript narrows `calendarBookingUrl` from `string | undefined`
    // to `string` inside the first branch (avoids TS2322 on `href`).
    if (calendarBookingUrl) {
      // Priority: external URL (Cal.com/Calendly) takes precedence.
      ctas.push({
        slug: 'calendarBooking',
        label: 'Agendar llamada',
        href: calendarBookingUrl,
        icon: Calendar,
        external: true,
      });
    } else if (availability?.enabled) {
      // Fallback: internal reservation system (SPEC-171).
      // `external: false` -> renderer skips `target="_blank"` so the
      // visitor stays in the same tab (SPA navigation to /{username}/reservar).
      ctas.push({
        slug: 'calendarBooking',
        label: 'Agendar llamada',
        href: `/${username}/reservar`,
        icon: Calendar,
        external: false,
      });
    }
    // If neither external URL nor internal availability -> CTA does not render
    // (silent fail — same behavior as before this fix).
  }

  // Sprint 85 fix (Founder directive): "Mi Market" renamed to "Referir al Market"
  // — this CTA is for REFERRING clients to the general Profileia Market (not
  // the user's specific store). It should ALWAYS show when enabled (no
  // auto-show conditions) because it links to /market (general catalog)
  // where the visitor can browse products + become a seller themselves.
  // The old condition (hasMarketProducts && storeSlug) was wrong — it
  // required the user to have their own products, but the CTA is about
  // referring visitors to the Market platform, not to the user's store.
  if (ctasConfig.myMarket) {
    ctas.push({
      slug: 'myMarket',
      label: 'Descubrir el Market',
      href: `/market?ref=${encodeURIComponent(username)}`,
      icon: ShoppingBag,
    });
  }

  // Sprint 85 NEW CTA: "Mi Tienda" — links to the user's specific store
  // page (/market/tienda/{storeSlug}). Fix (2026-10-24): removed the
  // hasMarketProducts gate — the store page exists even without products
  // (shows an empty state). The duplicate block below was already fixed;
  // this one is removed to avoid double-adding.
  // (Moved to the block after whatsappDirect below.)

  // P-279 auto-show: only render "WhatsApp Directo" if user has whatsapp set
  if (ctasConfig.whatsappDirect && whatsappNumber) {
    // PR #1077 fix: normalize the whatsapp number for wa.me link.
    // wa.me requires digits only with country code — no spaces, +, -, or ().
    // If the user enters "+57 300 123 4567" we strip everything to "573001234567".
    // If the user enters "3001234567" (no country code) we can't add one
    // automatically, but wa.me still works — it opens WhatsApp with the
    // number as-is (user may need to add country code manually in WA).
    const normalizedWhatsApp = whatsappNumber.replace(/[^\d]/g, '');
    ctas.push({
      slug: 'whatsappDirect',
      label: 'Escríbeme por WhatsApp',
      href: `https://wa.me/${normalizedWhatsApp}`,
      icon: MessageCircle,
      external: true,
    });
  }

  // Fix (2026-10-24): showProfileiaCta was defined in the type but NEVER
  // rendered. The 'Quiero ser parte de Profileia' CTA uses the referralCode
  // to build a /r/{code} link (falls back to /register if empty).
  if (ctasConfig.showProfileiaCta) {
    ctas.push({
      slug: 'showProfileiaCta',
      label: 'Quiero ser parte de Profileia',
      href: referralCode ? `/r/${encodeURIComponent(referralCode)}` : '/register',
      icon: UserPlus,
      external: false,
    });
  }

  // Fix (2026-10-24): myStore was gated on hasMarketProducts && storeSlug.
  // The store page always exists (created at seller onboarding). Show the
  // CTA if myStore is enabled, even without products — the store page
  // shows an empty state that's still useful for branding.
  if (ctasConfig.myStore && storeSlug) {
    ctas.push({
      slug: 'myStore',
      label: 'Mi Tienda',
      href: `/market/tienda/${encodeURIComponent(storeSlug)}`,
      icon: Store,
      external: false,
    });
  }

  // V108 Bug #3 PR2: iterate customCtas array (max 5). Each enabled item
  // with non-empty url + label renders as a separate CTA. Slug is
  // `customCta-${index}` so click tracking (POST /api/public/:username/click
  // with ctaSlug) gets a unique slug per CTA — analytics can break down
  // clicks per custom CTA, not just "customCta" as a single bucket.
  // Pre-fix this was a single `customCta` object (one CTA max).
  if (Array.isArray(ctasConfig.customCtas)) {
    ctasConfig.customCtas.forEach((cta, index) => {
      if (cta?.enabled && cta.url && cta.label) {
        ctas.push({
          slug: `customCta-${index}`,
          label: cta.label,
          href: cta.url,
          icon: LinkIcon,
          external: true,
        });
      }
    });
  }

  if (ctas.length === 0) return null;

  // Click tracking helper — fire-and-forget POST. Best-effort, never blocks UX.
  const trackClick = (ctaSlug: string) => {
    if (typeof window === 'undefined') return;
    fetch(`/api/public/${encodeURIComponent(username)}/click`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ target: 'cta', ctaSlug }),
    }).catch(() => {
      // Silent — analytics must NEVER break the UX.
    });
  };

  return (
    <section
      aria-labelledby="hub-ctas-heading"
      className="mt-6 pt-6 border-t border-slate-700"
    >
      <h2
        id="hub-ctas-heading"
        className="text-sm font-semibold text-white inline-flex items-center gap-2 mb-3"
      >
        <LinkIcon className="size-4" aria-hidden="true" />
        Acciones
      </h2>
      {/* Regular CTAs in a 2-col grid (excluding calendarBooking which
          gets a prominent full-width button below). */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {ctas.filter((cta) => cta.slug !== 'calendarBooking').map((cta) => {
          const Icon = cta.icon;
          // SPEC-173: if this is the myLandings CTA + user has multiple
          // landings, render a <select> dropdown instead of a simple link.
          if (cta.slug === 'myLandings' && landings.length > 1) {
            return (
              <div
                key={cta.slug}
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg bg-slate-800/60 border border-slate-700 text-slate-200 hover:bg-slate-700/60 hover:border-slate-600 transition"
              >
                <Icon className="size-4" aria-hidden="true" />
                <select
                  className="bg-transparent text-slate-200 text-sm font-medium outline-none cursor-pointer flex-1"
                  defaultValue={cta.href}
                  onChange={(e) => {
                    if (e.target.value) window.open(e.target.value, '_blank', 'noopener,noreferrer');
                  }}
                  aria-label="Seleccionar landing page"
                >
                  <option value={`/l/${encodeURIComponent(username)}`} className="bg-slate-800">
                    Mis Landing Pages — Ver todas
                  </option>
                  {landings.map(l => (
                    <option
                      key={l.slug}
                      value={`/l/${encodeURIComponent(username)}/${l.slug}`}
                      className="bg-slate-800"
                    >
                      {l.title}
                    </option>
                  ))}
                </select>
              </div>
            );
          }
          // V126-F8 fix (Founder directive): ALL CTAs open in a new tab so
          // the Hub profile card is NOT replaced. Pre-fix: only external
          // CTAs (WhatsApp, Calendly, custom) opened in new tabs; internal
          // CTAs (Plan de Compensación, Mis Landings, Próxima Sala, Mi
          // Market, Mi Tienda, Reservar) navigated in the same tab,
          // replacing the card. Post-fix: ALL CTAs use target="_blank".
          // The `ExternalLink` icon is still shown ONLY for truly external
          // links (different domain) — internal links opening in new tabs
          // is the expected behavior, not a visual signal.
          const isExternal = cta.external === true;
          return (
            <a
              key={cta.slug}
              href={cta.href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackClick(cta.slug)}
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg bg-slate-800/60 border border-slate-700 text-slate-200 hover:bg-slate-700/60 hover:border-slate-600 transition"
            >
              <Icon className="size-4" aria-hidden="true" />
              <span>{cta.label}</span>
              {isExternal && (
                <ExternalLink className="size-3 ml-1 opacity-60" aria-hidden="true" />
              )}
            </a>
          );
        })}
      </div>

      {/* calendarBooking CTA — prominent full-width button below the grid.
          Fix (2026-10-24): Founder directive "el boton de agendar llamada
          debería aparecer abajo con un mejor diseño". Now rendered as a
          full-width gradient button with larger padding + glow shadow. */}
      {ctas.find((cta) => cta.slug === 'calendarBooking') && (() => {
        const bookingCta = ctas.find((cta) => cta.slug === 'calendarBooking')!;
        const BookingIcon = bookingCta.icon;
        const bookingExternal = bookingCta.external === true;
        return (
          <a
            href={bookingCta.href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackClick('calendarBooking')}
            className="mt-3 inline-flex w-full items-center justify-center gap-2.5 px-6 py-3.5 text-base font-semibold text-white rounded-xl bg-gradient-to-r from-[#06B6D4] to-[#2563EB] shadow-lg shadow-[#06B6D4]/30 hover:shadow-[#06B6D4]/50 hover:scale-[1.01] active:scale-[0.99] transition-transform motion-reduce:transform-none"
          >
            <BookingIcon className="size-5" aria-hidden="true" />
            <span>{bookingCta.label}</span>
            {bookingExternal && (
              <ExternalLink className="size-3.5 ml-1 opacity-70" aria-hidden="true" />
            )}
          </a>
        );
      })()}
    </section>
  );
}
